<template>
  <div
    v-if="isDialerVisible"
    class="app-dialer-popup"
  >
    <app-dialer-main-popup-container
      ref="main-popup"
      :is-info-active="isInfoActive"
      :is-keypad-active="isKeypadActive"
      :is-members-popup-active="isMembersPopupActive"
      :is-invite-popup-active="isInvitePopupActive"
      :is-transfer-popup-active="isTransferPopupActive"
      @toggle-info="onTogglePopup('isInfoActive')"
      @toggle-keypad="onTogglePopup('isKeypadActive')"
      @toggle-members-popup="onTogglePopup('isMembersPopupActive')"
      @toggle-invite-popup="onTogglePopup('isInvitePopupActive')"
      @toggle-transfer-popup="onTogglePopup('isTransferPopupActive')"
    />

    <app-dialer-keypad-container
      v-if="isKeypadActive"
      ref="keypad-popup"
      component="popup"
      @close="onPopupClose"
    />

    <app-dialer-info-popup-container
      v-if="isInfoActive"
      ref="info-popup"
      @close="onPopupClose"
    />

    <app-dialer-call-members-container
      v-if="isMembersPopupActive"
      ref="members-popup"
      component="popup"
      @close="onPopupClose"
    />

    <app-dialer-actions-popup-container
      v-if="isInvitePopupActive || isTransferPopupActive"
      ref="actions-popup"
      :actions-type="actionsType"
      @close="onPopupClose"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue-demi';

import AppDialerMainPopupContainer from '../../containers/popups/AppDialerMainPopupContainer.vue';
import AppDialerKeypadContainer from '../../containers/common/AppDialerKeypadContainer.vue';
import AppDialerInfoPopupContainer from '../../containers/popups/AppDialerInfoPopupContainer.vue';
import AppDialerCallMembersContainer from '../../containers/common/AppDialerCallMembersContainer.vue';
import AppDialerActionsPopupContainer from '../../containers/popups/AppDialerActionsPopupContainer.vue';
import {
  useDialerStore,
  useDialerActions,
  useDialerReconnectConference,
} from '../../composables';

export default defineComponent({
  name: 'AppDialerPopup',
  components: {
    AppDialerMainPopupContainer,
    AppDialerKeypadContainer,
    AppDialerInfoPopupContainer,
    AppDialerCallMembersContainer,
    AppDialerActionsPopupContainer,
  },
  setup() {
    const { isDialerVisible } = useDialerStore();
    const { subscribeRingtone, subscribeToIsVisible } = useDialerActions();
    const { subscribeToReconnectFeature } = useDialerReconnectConference();

    subscribeRingtone();
    subscribeToReconnectFeature();
    subscribeToIsVisible();
    return { isDialerVisible };
  },
  data() {
    return {
      isInfoActive: false,
      isKeypadActive: false,
      isMembersPopupActive: false,
      isInvitePopupActive: false,
      isTransferPopupActive: false,
    };
  },
  computed: {
    actionsType(): string {
      return this.isInvitePopupActive
        ? 'invite'
        : this.isTransferPopupActive
          ? 'transfer'
          : '';
    },
  },
  watch: {
    isDialerVisible() {
      this.onPopupClose();
    },
  },
  methods: {
    onPopupClose() {
      this.isInfoActive = false;
      this.isKeypadActive = false;
      this.isMembersPopupActive = false;
      this.isInvitePopupActive = false;
      this.isTransferPopupActive = false;
    },

    onTogglePopup(property: string) {
      const PROPERTY_TO_REF: { [property: string]: string; } = {
        isInfoActive: 'info-popup',
        isKeypadActive: 'keypad-popup',
        isMembersPopupActive: 'members-popup',
        isInvitePopupActive: 'actions-popup',
        isTransferPopupActive: 'actions-popup',
      };

      const current = (this as any)[property];
      this.onPopupClose();

      (this as any)[property] = !current;
      if ((this as any)[property]) {
        this.$nextTick(() => {
          this.setInitialPopupPosition(PROPERTY_TO_REF[property]);
        });
      }
    },

    /**
     * On initial open set styles for popup,
     * so it is centered slightly above or below main dialer popup  .
     */
    setInitialPopupPosition(popupKey: string) {
      const POPUP_MARGIN = 8;

      const appDialerRef = this.$refs['main-popup'] as Vue;
      const popupRef = this.$refs[popupKey] as Vue;
      const popupEl = popupRef.$el as HTMLElement;

      const appDialerBox = appDialerRef.$el.getBoundingClientRect();
      const popupBox = popupEl.getBoundingClientRect();

      let top = 0;
      let left = 0;

      const spaceAtBottom = window.innerHeight - appDialerBox.bottom;
      const hasEnoughSpaceAtBottom = spaceAtBottom >= popupBox.height / 2;

      /* Position popup in the center if it can't fit below the main popup */
      if (!hasEnoughSpaceAtBottom) {
        top = window.innerHeight / 2 - popupBox.height / 2;
        left = window.innerWidth / 2 - popupBox.width / 2;
      } else {
        top = appDialerBox.bottom + POPUP_MARGIN;
        left = appDialerBox.left + appDialerBox.width / 2 - popupBox.width / 2;
      }

      popupEl.style.position = 'fixed';
      popupEl.style.top = `${top}px`;
      popupEl.style.left = `${left}px`;
      popupEl.style.marginRight = 'none';
      popupEl.style.transform = 'none';
    },
  },
});
</script>
